<template>
  <div class="w-full">
    <div
      class="w-full border min-h-[260px] p-5 relative bg-white flex justify-center items-center"
      :class="{
        'border border-danger border-dashed': error,
        'border-gray-300': !error,
      }"
    >
      <template v-if="!files.length">
        <div
          class="h-full w-full flex items-center justify-center flex-col cursor-pointer group"
          @click="chooseFile()"
          :class="{ 'cursor-not-allowed pointer-events-none': loading }"
        >
          <IconsLoadingDecorator class="w-12" v-if="loading" />
          <nuxt-img
            loading="lazy"
            v-else
            src="/icons/img-upload.png"
            alt="upload-images"
            :width="64"
          />
          <p
            class="mt-2.5 mb-4 font-semibold group-hover:text-success text-base transition-colors ease-in duration-100"
          >
            {{ dataLocales.data.title }}
          </p>
          <p class="text-gray-700 text-sm">
            {{ dataLocales.data.description }} (max: 10 files & 12MB/file)
          </p>
          <div class="h-8">
            <p class="text-sm text-danger mt-2" v-if="error">
              {{ dataLocales.data.err_msg }}
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="h-full w-full flex items-center gap-4 bg-white justify-start flex-wrap cursor-pointer group"
        >
          <ClientOnly>
            <draggable
              v-model="files"
              group="images"
              :disabled="qrcreate.imageData"
              @start="drag = true"
              @end="drag = false"
              item-key="id"
              class="flex items-center gap-4 bg-white justify-start flex-wrap"
            >
              <template #item="{ element }">
                <div
                  class="w-36 h-36 overflow-hidden border-dashed border border-gray-300 bg-gray-100 rounded-sm flex items-center flex-col justify-center gap-2 relative"
                >
                  <XIcon
                    v-if="!qrcreate.imageData"
                    @click="DeleteFile(element)"
                    class="w-5 h-5 cursor-pointer absolute text-gray-500 hover:text-black top-1 right-1"
                  />
                  <img
                    class="h-full w-full object-cover object-center"
                    :src="element.path"
                    alt=""
                  />
                </div>
              </template>
            </draggable>
          </ClientOnly>
          <div
            v-if="!qrcreate.imageData"
            @click="chooseFile()"
            :class="{ 'cursor-not-allowed pointer-events-none': loading }"
            class="w-36 h-36 border-dashed border border-gray-300 bg-gray-100 rounded-sm flex items-center flex-col justify-center gap-2"
          >
            <IconsLoadingDecorator class="w-8" v-if="loading" />
            <PlusIcon stroke-width="1" size="32" v-else />
            <span class="text-sm"> {{ dataLocales.data.button }}</span>
          </div>
        </div>
      </template>
      <input
        type="file"
        ref="file"
        accept="image/*"
        multiple
        class="hidden"
        @change="changeFile"
      />
    </div>
    <partials-expired-file-notice />
    <PartialsLinkQrcode />
    <tools-button-create @generate="generate" @clearSession="clearSession" />
  </div>
</template>

<script lang="ts">
import { useQrCreateStore } from "~/stores/qrcreate";
import UploadMixin from "./upload.mixin";
import { notify } from "@kyvg/vue3-notification";
import DataLocale from "~~/config/interface/DataLocale";
import { useLimit } from "~~/stores/limit";

export default defineComponent({
  mixins: [UploadMixin],
  data() {
    return {
      type: "images",
    };
  },
  setup() {
    const qrcreate = useQrCreateStore();
    const error = ref(false);
    const dataLocales = new DataLocale([
      {
        locale: "en",
        title: "Upload images",
        description: "Please click here to upload pictures ",
        err_msg: "Please click here to upload the file first",
        button: " Add Pictures",
      },
      {
        locale: "vi",
        title: "Tải lên hình ảnh",
        description: "Vui lòng nhấp vào đây để tải lên hình ảnh",
        err_msg: "Vui lòng nhấp vào đây để tải tệp lên trước",
        button: "Thêm hình ảnh",
      },
      {
        locale: "ja",
        title: "画像をアップロードする",
        description: "ここをクリックして画像をアップロードしてください",
        err_msg: "ファイルをアップロードするには、ここをクリックしてください",
        button: "画像を追加する",
      },
      {
        locale: "zh",
        title: "上传图片",
        description: "请点击此处上传图片",
        err_msg: "请先点击此处上传文件",
        button: "添加图片",
      },
      {
        locale: "de",
        title: "Bilder hochladen",
        description: "Bitte klicken Sie hier, um Bilder hochzuladen",
        err_msg: "Bitte klicken Sie hier, um zuerst die Datei hochzuladen",
        button: "Bilder hinzufügen",
      },
      {
        locale: "fr",
        title: "Télécharger des images",
        description: "Veuillez cliquer ici pour télécharger des images",
        err_msg: "Veuillez cliquer ici pour télécharger le fichier d'abord",
        button: "Ajouter des images",
      },
      {
        locale: "it",
        title: "Carica immagini",
        description: "Fare clic qui per caricare le immagini",
        err_msg: "Fare clic qui per caricare il file prima",
        button: "Aggiungi immagini",
      },
      {
        locale: "nl",
        title: "Afbeeldingen uploaden",
        description: "Klik hier om afbeeldingen te uploaden",
        err_msg: "Klik hier om eerst het bestand te uploaden",
        button: "Afbeeldingen toevoegen",
      },
      {
        locale: "pt",
        title: "Carregar imagens",
        description: "Clique aqui para carregar imagens",
        err_msg: "Clique aqui para carregar o arquivo primeiro",
        button: "Adicionar imagens",
      },
      {
        locale: "es",
        title: "Cargar imágenes",
        description: "Haga clic aquí para cargar imágenes",
        err_msg: "Haga clic aquí para cargar el archivo primero",
        button: "Añadir imágenes",
      },
    ]);
    const session = ref<{
      FileObject?: {
        mime_type?: string;
        path?: string;
      };
      id: number;
      slug?: string;
      type?: string;
    }>();
    const file = ref<HTMLElement>();
      const limit = useLimit()
    return {
      qrcreate,
      error,
      session,
      file,
      dataLocales,
      limit
    };
  },
  methods: {
    chooseFile() {
      this.error = false;
      (this.$refs.file as HTMLInputElement).click();
    },
    validateData() {
      if (this.files.length) {
        return true;
      }
      this.error = true;
      return false;
    },
    async generate() {
      if (this.validateData()) {
        await this.qrcreate.CreateSession(this.type, this.files);
      }
    },
    changeFile(e: Event) {
      if (this.limit.checkLimit("image")) {
        return;
      }
      var files = (e.target as HTMLInputElement).files;
      if (files?.length) {
        //validate size
        var formdata = new FormData();
        for (let i = 0; i < files.length; i++) {
          if (files[i].size > 12 * 1024 * 1024) {
            notify({
              type: "warn",
              text: "Please reduce its size to less than 12mb",
              title: "Sorry, the file has exceeded the upload limit",
            });
            return;
          }
          formdata.append("files", files[i]);
          if (i == 9) {
            break;
          }
        }
        this.UploadFiles(formdata, true);

        (this.$refs.file as HTMLInputElement).value = "";
      }
    },
  },
});
</script>
